// Textures generated at load time on a canvas: no image files to download or
// license. Track UVs are in metres, so each texture is scaled to its tile size.

import { DynamicTexture, Scene } from './babylon';

/** Texture.WRAP_ADDRESSMODE. DynamicTexture clamps by default, which smears tiled surfaces. */
const WRAP = 1;

/** Small deterministic PRNG so the textures look the same on every load. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const cache = new WeakMap<Scene, Map<string, DynamicTexture>>();

function make(scene: Scene, name: string, size: number, tileMetres: number, draw: (ctx: CanvasRenderingContext2D, size: number) => void, tileV = tileMetres): DynamicTexture {
  // Materials that share a texture (road and run-off asphalt) share one copy.
  let byName = cache.get(scene);
  if (!byName) cache.set(scene, (byName = new Map()));
  const cached = byName.get(name);
  if (cached) return cached;
  const tex = new DynamicTexture(name, { width: size, height: size }, scene, true);
  byName.set(name, tex);
  draw(tex.getContext() as CanvasRenderingContext2D, size);
  tex.update(true);
  tex.wrapU = WRAP;
  tex.wrapV = WRAP;
  tex.uScale = 1 / tileMetres;
  tex.vScale = 1 / tileV;
  // Track surfaces are seen at grazing angles from a low camera.
  tex.anisotropicFilteringLevel = 8;
  return tex;
}

/** Speckled noise: many tiny dots of slightly lighter/darker tone. */
function speckle(ctx: CanvasRenderingContext2D, size: number, rand: () => number, base: [number, number, number], spread: number, count: number, maxR: number): void {
  ctx.fillStyle = `rgb(${base.join(',')})`;
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < count; i++) {
    const d = (rand() - 0.5) * spread;
    const r = Math.max(0, Math.min(255, base[0] + d));
    const g = Math.max(0, Math.min(255, base[1] + d));
    const b = Math.max(0, Math.min(255, base[2] + d));
    ctx.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`;
    const rad = 0.5 + rand() * maxR;
    ctx.fillRect(rand() * size, rand() * size, rad, rad);
  }
}

/** Fine-grained asphalt with a few darker patches; neutral so tint comes from the material. */
export function asphaltTexture(scene: Scene): DynamicTexture {
  return make(scene, 'tex-asphalt', 512, 4, (ctx, size) => {
    const rand = rng(1);
    speckle(ctx, size, rand, [128, 128, 130], 70, 26000, 1.6);
    // Faint repaired patches and tar seams.
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = `rgba(60,60,64,${0.05 + rand() * 0.06})`;
      ctx.fillRect(rand() * size, rand() * size, 40 + rand() * 120, 20 + rand() * 60);
    }
  });
}

/** Mown grass: noise plus alternating light/dark stripes along the track. */
export function grassTexture(scene: Scene): DynamicTexture {
  return make(
    scene,
    'tex-grass',
    256,
    6,
    (ctx, size) => {
      const rand = rng(2);
      speckle(ctx, size, rand, [118, 168, 82], 40, 9000, 1.5);
      ctx.fillStyle = 'rgba(255,255,255,0.07)';
      ctx.fillRect(0, 0, size, size / 2);
    },
    12,
  );
}

export function gravelTexture(scene: Scene): DynamicTexture {
  return make(scene, 'tex-gravel', 256, 2, (ctx, size) => {
    const rand = rng(3);
    speckle(ctx, size, rand, [196, 178, 140], 90, 14000, 2.4);
  });
}

/**
 * Barrier walls dressed as advertising boards: coloured panels with generic
 * text. Barrier UVs are (distance along, height 0..1 m).
 */
export function barrierTexture(scene: Scene): DynamicTexture {
  const panels: Array<[string, string, string]> = [
    ['#e10600', '#ffffff', 'FONE GP'],
    ['#ffffff', '#111111', 'SEPANG'],
    ['#0b2a5b', '#ffcc33', '2026'],
    ['#ffcc33', '#111111', 'TILT TO STEER'],
    ['#1b1f2a', '#ffffff', 'FONE GP'],
    ['#ffffff', '#e10600', 'STRAIGHT MODE'],
  ];
  const tex = new DynamicTexture('tex-barrier', { width: 1024, height: 64 }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  const w = 1024 / panels.length;
  panels.forEach(([bg, fg, text], i) => {
    ctx.fillStyle = bg;
    ctx.fillRect(i * w, 0, w, 64);
    ctx.fillStyle = fg;
    ctx.font = 'bold 34px Arial, Helvetica, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, i * w + w / 2, 34, w - 12);
  });
  // Dark bottom edge, like a kerb-side scuff line.
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fillRect(0, 58, 1024, 6);
  tex.update(true);
  // One full set of panels every 36 m along; full height (UV v 0..1 m).
  tex.wrapU = WRAP;
  tex.uScale = 1 / 36;
  tex.vScale = 1;
  tex.anisotropicFilteringLevel = 4;
  return tex;
}

/** Soft radial blob for the fake car shadow. */
export function blobShadowTexture(scene: Scene): DynamicTexture {
  const tex = new DynamicTexture('tex-blob', { width: 128, height: 128 }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  const g = ctx.createRadialGradient(64, 64, 6, 64, 64, 64);
  g.addColorStop(0, 'rgba(0,0,0,0.75)');
  g.addColorStop(0.6, 'rgba(0,0,0,0.45)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  tex.hasAlpha = true;
  tex.update(true);
  return tex;
}

/**
 * Tangent-space normal map for asphalt (Ultra): a tileable height field of
 * packed aggregate stones, so the low sun catches individual grains. Tiled at
 * the same 4 m as {@link asphaltTexture}, with no visual link to its speckle.
 */
export function asphaltNormalTexture(scene: Scene): DynamicTexture {
  const size = 512;
  return make(scene, 'tex-asphalt-normal', size, 4, (ctx) => {
    const rand = rng(21);
    const h = new Float32Array(size * size);
    // Stones: rounded bumps 1-3 px in radius (≈ 10-45 mm across at 4 m per 512 px), wrapping at the edges.
    for (let i = 0; i < 52000; i++) {
      const cx = rand() * size;
      const cy = rand() * size;
      const r = 0.7 + rand() * rand() * 2.2;
      const top = 0.4 + rand() * 0.6;
      const r2 = r * r;
      for (let dy = -Math.ceil(r); dy <= Math.ceil(r); dy++) {
        for (let dx = -Math.ceil(r); dx <= Math.ceil(r); dx++) {
          const d2 = dx * dx + dy * dy;
          if (d2 > r2) continue;
          const x = (Math.floor(cx) + dx + size) % size;
          const y = (Math.floor(cy) + dy + size) % size;
          const v = top * Math.sqrt(1 - d2 / r2);
          const k = y * size + x;
          if (v > h[k]!) h[k] = v;
        }
      }
    }
    const img = ctx.createImageData(size, size);
    const at = (x: number, y: number): number => h[((y + size) % size) * size + ((x + size) % size)]!;
    const strength = 1.4;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const nx = (at(x - 1, y) - at(x + 1, y)) * strength;
        const ny = (at(x, y - 1) - at(x, y + 1)) * strength;
        const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1);
        const i = (y * size + x) * 4;
        img.data[i] = Math.round((nx * inv * 0.5 + 0.5) * 255);
        img.data[i + 1] = Math.round((ny * inv * 0.5 + 0.5) * 255);
        img.data[i + 2] = Math.round((inv * 0.5 + 0.5) * 255);
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  });
}
